import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Card,
  Input,
  Message,
  Modal,
  Space,
  Table,
  Tag,
  Typography,
} from '@arco-design/web-react';
import { IconPlus, IconRefresh } from '@arco-design/web-react/icon';
import { useNavigate } from 'react-router-dom';
import { upstreamLabApi } from '@/api/upstreamLab';
import type { CatalogResponse, LabSession, ScenarioTemplate } from '@/types/upstreamLab';

export default function SessionListView() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState<LabSession[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [createVisible, setCreateVisible] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNote, setNewNote] = useState('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([upstreamLabApi.listSessions(page, 20), upstreamLabApi.getCatalog()])
      .then(([list, cat]) => {
        setSessions(list.items);
        setTotal(list.total);
        setCatalog(cat);
      })
      .catch(() => {
        setSessions([]);
      })
      .finally(() => setLoading(false));
  }, [page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (template?: ScenarioTemplate) => {
    const name = newName.trim() || template?.name || `联调 ${new Date().toLocaleString()}`;
    if (!name) {
      Message.warning('请输入会话名称');
      return;
    }
    setCreating(true);
    try {
      const note = newNote.trim() || template?.description || undefined;
      const session = await upstreamLabApi.createSession({ name, note });
      Message.success('会话已创建');
      setCreateVisible(false);
      setNewName('');
      setNewNote('');
      navigate(`/upstream-lab/${session.id}`);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {catalog && !catalog.environment.isDev && (
          <Card style={{ borderColor: 'rgb(var(--orange-6))' }}>
            <Typography.Text type="warning">
              当前为生产环境（{catalog.environment.appEnv}）。写操作需在工作台勾选确认后再执行。
            </Typography.Text>
          </Card>
        )}
        {catalog && catalog.environment.useMock && (
          <Card style={{ borderColor: 'rgb(var(--green-6))' }}>
            <Typography.Text type="success">
              当前走同进程 Mock 上游（{catalog.environment.appEnv}），不会调用真实 Newgen。
            </Typography.Text>
          </Card>
        )}

        <Space align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
          <div>
            <Typography.Title heading={4} style={{ margin: 0 }}>
              上游联调台
            </Typography.Title>
            <Typography.Text type="secondary">
              按 Newgen Issuing Guide 逐接口人工联调，会话内产物可串联后续调用
            </Typography.Text>
          </div>
          <Space>
            <Button icon={<IconRefresh />} onClick={load}>
              刷新
            </Button>
            <Button type="primary" icon={<IconPlus />} onClick={() => setCreateVisible(true)}>
              新建会话
            </Button>
          </Space>
        </Space>

        {catalog && catalog.scenarioTemplates.length > 0 && (
          <Card title="场景模板（快速新建）">
            <Space wrap>
              {catalog.scenarioTemplates.map((t) => (
                <Button
                  key={t.id}
                  onClick={() => {
                    setNewName(t.name);
                    setNewNote(t.note ? `${t.description}\n${t.note}` : t.description);
                    setCreateVisible(true);
                  }}
                >
                  {t.name}
                </Button>
              ))}
            </Space>
          </Card>
        )}

        <Card>
          <Table
            loading={loading}
            rowKey="id"
            data={sessions}
            pagination={{
              current: page,
              total,
              pageSize: 20,
              onChange: setPage,
            }}
            columns={[
              { title: '名称', dataIndex: 'name' },
              { title: '创建人', dataIndex: 'createdBy', width: 120 },
              {
                title: '产物数',
                width: 100,
                render: (_, row) => Object.keys(row.artifacts || {}).length,
              },
              {
                title: '环境',
                width: 100,
                render: () => (
                  <Tag color={catalog?.environment.useMock ? 'green' : 'orange'}>
                    {catalog?.environment.useMock
                      ? 'dev/mock'
                      : (catalog?.environment.appEnv ?? '-')}
                  </Tag>
                ),
              },
              {
                title: '更新时间',
                dataIndex: 'updatedAt',
                width: 180,
                render: (v: string) => new Date(v).toLocaleString(),
              },
              {
                title: '操作',
                width: 120,
                render: (_, row) => (
                  <Button type="text" onClick={() => navigate(`/upstream-lab/${row.id}`)}>
                    进入工作台
                  </Button>
                ),
              },
            ]}
          />
        </Card>
      </Space>

      <Modal
        title="新建联调会话"
        visible={createVisible}
        confirmLoading={creating}
        onOk={() => handleCreate()}
        onCancel={() => setCreateVisible(false)}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Input placeholder="会话名称" value={newName} onChange={setNewName} />
          <Input.TextArea
            placeholder="备注（可选）"
            value={newNote}
            onChange={setNewNote}
            autoSize={{ minRows: 2, maxRows: 4 }}
          />
        </Space>
      </Modal>
    </div>
  );
}
